/**
 * AIReviewTab (Tab 6) — RAG ingestion editor for broker chatbot.
 *
 * This is NOT an approval queue. It's where brokers enrich AI-generated
 * document metadata so the Broker Chat Bot's RAG pipeline can retrieve
 * structured context at runtime.
 *
 * 3 top-level sub-tabs (matches original AIDocumentReview.tsx):
 *   1. Uploaded Documents  — real docs uploaded via Tab 5 (Documents)
 *   2. Document Templates  — pre-built skeleton templates for common docs
 *   3. Compliance Rules    — brokerage-specific compliance rule sets
 *
 * For each selected document, 6 editor sub-tabs:
 *   1. Summary & Purpose       — summary text + document purpose
 *   2. Key Information         — parties, dates, terms, transaction types
 *   3. Usage Context           — when required, timing conditions, stages
 *   4. Compliance              — legal refs, CASA, state-specific
 *   5. Classification          — category, subcategory, frequency
 *   6. Q&A & Keywords          — search keywords + chatbot QA pairs (RAG feed)
 *
 * Backend:
 *   GET /documents/my-documents?owner_type=brokerage&brokerage_id=... [&is_placeholder=true]
 *   PUT /documents/:id/ai-analysis  — save enriched AIAnalysis
 *   POST /documents/create-template — create new template
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faListCheck,
  faCog,
  faRobot,
  faKey,
  faLightbulb,
  faClipboardCheck,
  faFilter,
  faQuestion,
  faPlus,
  faXmark,
  faFloppyDisk,
  faTriangleExclamation,
  faCheckCircle,
  faPen,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../../helpers/api';
import {
  SplitPanel,
  SearchBar,
  FilterPills,
  ListItem,
  ProfileHeader,
  DetailTabs,
  NavyInput,
  NavyTextarea,
  GoldButton,
  OutlineButton,
  colors,
  fonts,
} from '../shared';

// ---------------------------------------------------------------------------
// Types — matches the original AIAnalysis interface from AIDocumentReview.tsx
// ---------------------------------------------------------------------------

interface AIAnalysis {
  summary?: string;
  document_purpose?: string;
  filename?: string;
  document_type?: string;
  enriched_at?: string;
  chatbot_qa?: Array<{ question: string; answer: string }>;
  key_information?: {
    parties_involved?: string[];
    effective_date?: string;
    critical_terms?: string[];
    document_purpose?: string;
    filing_deadline?: string;
  };
  usage_context?: {
    when_required?: {
      always_required?: boolean;
      transaction_stages?: string[];
      timing_conditions?: Array<{
        trigger?: string;
        deadline?: string;
        priority?: string;
      }>;
    };
    who_needs?: {
      signing_parties?: string[];
      reviewing_parties?: string[];
    };
    why_important?: {
      legal_requirements?: string[];
      protections_provided?: string[];
      risks_if_missing?: string[];
    };
  };
  search_keywords?: string[];
  compliance_requirements?: {
    legal_references?: string[];
    casa_compliance?: boolean;
    state_specific?: boolean;
  };
  document_classification?: {
    primary_category?: string;
    subcategory?: string;
    transaction_types?: string[];
    required_frequency?: string;
  };
  ai_confidence?: number;
  last_updated?: string;
}

interface DocumentRecord {
  id: string;
  document_type: string;
  filename: string;
  original_filename?: string;
  s3_url?: string;
  ai_analysis?: AIAnalysis | string;
  created_at: string;
  updated_at: string;
  is_active?: boolean;
  is_placeholder?: boolean;
  description?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOP_TABS = [
  { key: 'uploaded',  label: 'Uploaded Documents', icon: faFileAlt },
  { key: 'templates', label: 'Document Templates', icon: faListCheck },
  { key: 'rules',     label: 'Compliance Rules',   icon: faCog },
];

const EDITOR_TABS = [
  { key: 'summary',        label: 'Summary',        icon: faRobot },
  { key: 'key-info',       label: 'Key Info',       icon: faKey },
  { key: 'usage',          label: 'Usage Context',  icon: faLightbulb },
  { key: 'compliance',     label: 'Compliance',     icon: faClipboardCheck },
  { key: 'classification', label: 'Classification', icon: faFilter },
  { key: 'qa-keywords',    label: 'Q&A / Keywords', icon: faQuestion },
];

const CLASSIFICATION_OPTIONS = [
  { key: 'all',          label: 'All' },
  { key: 'required',     label: 'Required' },
  { key: 'conditional',  label: 'Conditional' },
  { key: 'optional',     label: 'Optional' },
  { key: 'supplemental', label: 'Supplemental' },
];

// Transaction stage filter — matches the pipeline stages in Usage Context.
// 'all' shows documents for any stage.
const STAGE_FILTER_OPTIONS = [
  { key: 'all',            label: 'All Stages' },
  { key: 'pre_contract',   label: 'Pre-Contract' },
  { key: 'showing',        label: 'Showing' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'closing',        label: 'Closing' },
  { key: 'cancelled',      label: 'Cancelled' },
];

// Transaction type filter — case-insensitive match against
// document_classification.transaction_types[] which stores values like
// 'Purchase', 'Listing', 'Rental', 'Referral', 'Land Contract', 'Commercial'
// (Title-cased — matches the original AIDocumentReview.tsx vocabulary).
const TYPE_FILTER_OPTIONS = [
  { key: 'all',           label: 'All Types' },
  { key: 'purchase',      label: 'Purchase' },
  { key: 'listing',       label: 'Listing' },
  { key: 'rental',        label: 'Rental' },
  { key: 'referral',      label: 'Referral' },
  { key: 'land contract', label: 'Land Contract' },
  { key: 'commercial',    label: 'Commercial' },
];

const TRANSACTION_STAGES = [
  { key: 'pre_contract',   label: 'Pre-Contract' },
  { key: 'showing',        label: 'Showing' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'closing',        label: 'Closing' },
  { key: 'cancelled',      label: 'Cancelled' },
];

// Exact set from the original AIDocumentReview.tsx (Title-cased, not slugs).
// These are the canonical broker-facing transaction types — don't rename them
// because the existing AI analysis JSON stores them in this exact shape.
const TRANSACTION_TYPES: Array<{ key: string; tooltip: string }> = [
  { key: 'Purchase',      tooltip: 'Buyer representation transactions' },
  { key: 'Listing',       tooltip: 'Seller representation transactions' },
  { key: 'Rental',        tooltip: 'Landlord/Tenant lease transactions' },
  { key: 'Referral',      tooltip: 'Agent-to-agent referral transactions' },
  { key: 'Land Contract', tooltip: 'Vendor/Vendee financing transactions' },
  { key: 'Commercial',    tooltip: 'Commercial property transactions' },
];

// Context-aware parties lookup — when a broker selects transaction types above,
// the Parties Involved button set changes to show only the relevant roles.
// Matches the original partiesByType map in AIDocumentReview.tsx.
const PARTIES_BY_TYPE: Record<string, string[]> = {
  'Purchase':      ['Buyer', 'Seller', 'Buyer Agent', 'Seller Agent', 'Brokerage', 'Escrow', 'Title Company', 'Lender', 'HOA'],
  'Listing':       ['Seller', 'Seller Agent', 'Brokerage', 'Buyer', 'Buyer Agent'],
  'Rental':        ['Landlord', 'Tenant', 'Landlord Agent', 'Tenant Agent', 'Property Manager', 'Brokerage'],
  'Referral':      ['Referring Agent', 'Receiving Agent', 'Referring Brokerage', 'Receiving Brokerage', 'Client'],
  'Land Contract': ['Vendor', 'Vendee', 'Vendor Agent', 'Vendee Agent', 'Brokerage', 'Title Company'],
  'Commercial':    ['Buyer/Tenant', 'Seller/Landlord', 'Buyer/Tenant Agent', 'Seller/Landlord Agent', 'Brokerage', 'Attorney', 'Title Company'],
};
const ALWAYS_COMMON_PARTIES = ['Brokerage', 'Attorney', 'Title Company'];
const DEFAULT_PARTIES_WHEN_NO_TYPES = [
  'Buyer', 'Seller', 'Buyer Agent', 'Seller Agent',
  'Landlord', 'Tenant', 'Landlord Agent', 'Tenant Agent',
  'Brokerage', 'Escrow', 'Title Company', 'Lender', 'HOA', 'Attorney', 'Property Manager',
];

const TIMING_TRIGGERS = [
  { group: 'Contract', options: [
    { key: 'upon_contract_acceptance', label: 'Upon Contract Acceptance' },
    { key: 'upon_offer_submission', label: 'Upon Offer Submission' },
    { key: 'upon_contract_execution', label: 'Upon Contract Execution' },
    { key: 'upon_mutual_acceptance', label: 'Upon Mutual Acceptance' },
  ]},
  { group: 'Inspection', options: [
    { key: 'upon_inspection_completion', label: 'Upon Inspection Completion' },
    { key: 'upon_inspection_request', label: 'Upon Inspection Request' },
    { key: 'upon_appraisal_order', label: 'Upon Appraisal Order' },
    { key: 'upon_appraisal_receipt', label: 'Upon Appraisal Receipt' },
  ]},
  { group: 'Financing', options: [
    { key: 'upon_loan_application', label: 'Upon Loan Application' },
    { key: 'upon_loan_approval', label: 'Upon Loan Approval' },
    { key: 'upon_financing_contingency_removal', label: 'Upon Financing Contingency Removal' },
    { key: 'upon_earnest_money_deposit', label: 'Upon Earnest Money Deposit' },
  ]},
  { group: 'Closing', options: [
    { key: 'prior_to_closing', label: 'Prior to Closing' },
    { key: 'at_closing', label: 'At Closing' },
    { key: 'after_closing', label: 'After Closing' },
    { key: 'upon_title_clearance', label: 'Upon Title Clearance' },
  ]},
  { group: 'Disclosure', options: [
    { key: 'upon_listing', label: 'Upon Listing' },
    { key: 'before_showing', label: 'Before Showing' },
    { key: 'with_purchase_agreement', label: 'With Purchase Agreement' },
    { key: 'upon_material_change', label: 'Upon Material Change' },
  ]},
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AIReviewTabProps {
  brokerageId?: string;
  brokerageName?: string;
}

const AIReviewTab: React.FC<AIReviewTabProps> = ({ brokerageId }) => {
  const effectiveBrokerageId = brokerageId || localStorage.getItem('brokerageId') || '';

  const [topTab, setTopTab] = useState<'uploaded' | 'templates' | 'rules'>('uploaded');
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState<DocumentRecord | null>(null);
  const [editorTab, setEditorTab] = useState('summary');
  const [editedAnalysis, setEditedAnalysis] = useState<AIAnalysis | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionAlert, setActionAlert] = useState<string | null>(null);

  // ---- Fetch ---------------------------------------------------------------
  const fetchDocuments = useCallback(async () => {
    if (!effectiveBrokerageId) {
      setLoading(false);
      return;
    }
    if (topTab === 'rules') {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        owner_type: 'brokerage',
        brokerage_id: effectiveBrokerageId,
      };
      if (topTab === 'templates') params.is_placeholder = true;
      const res = await axiosInstance.get('/documents/my-documents', { params });
      if (res.data?.success) {
        const docs: DocumentRecord[] = res.data.documents || [];
        setDocuments(docs);
        if (docs.length > 0 && !selected) {
          selectDocument(docs[0]);
        }
      } else {
        setError(res.data?.error || 'Failed to load documents');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBrokerageId, topTab]);

  useEffect(() => {
    setSelected(null);
    setEditedAnalysis(null);
    fetchDocuments();
  }, [fetchDocuments]);

  // ---- Selection + analysis parsing ---------------------------------------
  const selectDocument = (doc: DocumentRecord) => {
    setSelected(doc);
    const raw = doc.ai_analysis;
    const parsed: AIAnalysis =
      typeof raw === 'string'
        ? (() => {
            try {
              return JSON.parse(raw);
            } catch {
              return {};
            }
          })()
        : raw || {};
    setEditedAnalysis(JSON.parse(JSON.stringify(parsed)));
    setEditorTab('summary');
  };

  // ---- Dirty detection -----------------------------------------------------
  const isDirty = useMemo(() => {
    if (!selected || !editedAnalysis) return false;
    const original = typeof selected.ai_analysis === 'string'
      ? (() => { try { return JSON.parse(selected.ai_analysis as string); } catch { return {}; } })()
      : selected.ai_analysis || {};
    return JSON.stringify(original) !== JSON.stringify(editedAnalysis);
  }, [selected, editedAnalysis]);

  // ---- Save ----------------------------------------------------------------
  const handleSave = async () => {
    if (!selected || !editedAnalysis) return;
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/documents/${selected.id}/ai-analysis`, {
        ai_analysis: editedAnalysis,
      });
      if (res.data?.success !== false) {
        setActionAlert('✅ AI analysis saved');
        // Update local state with saved analysis
        setDocuments((docs) =>
          docs.map((d) => (d.id === selected.id ? { ...d, ai_analysis: editedAnalysis } : d))
        );
        setSelected((prev) => (prev ? { ...prev, ai_analysis: editedAnalysis } : prev));
      } else {
        setActionAlert(`❌ ${res.data?.error || 'Save failed'}`);
      }
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Save failed'}`);
    } finally {
      setSaving(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  // ---- Deep updater for nested paths --------------------------------------
  const updateAnalysis = (path: string, value: any) => {
    if (!editedAnalysis) return;
    const next = JSON.parse(JSON.stringify(editedAnalysis));
    const keys = path.split('.');
    let target: any = next;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!target[keys[i]]) target[keys[i]] = {};
      target = target[keys[i]];
    }
    target[keys[keys.length - 1]] = value;
    setEditedAnalysis(next);
  };

  // ---- Derived -------------------------------------------------------------
  const filtered = useMemo(() => {
    return documents.filter((d) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const hit =
          d.filename?.toLowerCase().includes(q) ||
          d.original_filename?.toLowerCase().includes(q) ||
          d.document_type?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (classificationFilter !== 'all' || stageFilter !== 'all' || typeFilter !== 'all') {
        const a = parseAnalysis(d.ai_analysis);

        if (classificationFilter !== 'all') {
          const cat = a?.document_classification?.primary_category?.toLowerCase();
          if (cat !== classificationFilter) return false;
        }

        if (stageFilter !== 'all') {
          const stages = a?.usage_context?.when_required?.transaction_stages || [];
          if (!stages.includes(stageFilter)) return false;
        }

        if (typeFilter !== 'all') {
          const types = (a?.document_classification?.transaction_types || []).map((t: string) =>
            (t || '').toLowerCase()
          );
          if (!types.includes(typeFilter)) return false;
        }
      }
      return true;
    });
  }, [documents, search, classificationFilter, stageFilter, typeFilter]);

  // ---- Render --------------------------------------------------------------
  return (
    <>
      {/* Top sub-tab bar (Uploaded / Templates / Rules) — stays fixed, doesn't scroll */}
      <div
        style={{
          background: colors.offBg,
          borderBottom: `1px solid ${colors.border}`,
          padding: '10px 24px',
          display: 'flex',
          gap: '6px',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        {TOP_TABS.map((t) => {
          const isActive = topTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTopTab(t.key as any)}
              style={{
                background: isActive ? `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)` : colors.white,
                color: isActive ? colors.navy : colors.muted,
                border: `1px solid ${isActive ? colors.gold : colors.border}`,
                borderRadius: '8px',
                padding: '10px 18px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: fonts.body,
                boxShadow: isActive ? '0 2px 8px rgba(201,149,42,0.25)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <FontAwesomeIcon icon={t.icon} style={{ fontSize: '12px' }} />
              {t.label}
            </button>
          );
        })}
      </div>

      {actionAlert && (
        <div
          style={{
            background: actionAlert.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: actionAlert.startsWith('✅') ? colors.green : colors.red,
            padding: '8px 24px',
            borderBottom: `1px solid ${colors.border}`,
            fontSize: '13px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {actionAlert}
        </div>
      )}

      {topTab === 'rules' ? (
        <ComplianceRulesPane />
      ) : (
        <SplitPanel
          left={
            <>
              <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
                <div style={{ marginBottom: '8px' }}>
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder={`Search ${topTab === 'templates' ? 'templates' : 'documents'}...`}
                  />
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <FilterPills
                    options={CLASSIFICATION_OPTIONS}
                    activeKey={classificationFilter}
                    onChange={setClassificationFilter}
                  />
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <FilterPills
                    options={STAGE_FILTER_OPTIONS}
                    activeKey={stageFilter}
                    onChange={setStageFilter}
                  />
                </div>
                <FilterPills
                  options={TYPE_FILTER_OPTIONS}
                  activeKey={typeFilter}
                  onChange={setTypeFilter}
                />
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                  <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>
                    Loading {topTab}…
                  </div>
                ) : error ? (
                  <div style={{ padding: '20px', color: colors.red, fontSize: '12px' }}>{error}</div>
                ) : filtered.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                    <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '32px', marginBottom: '8px' }} />
                    <div style={{ fontSize: '13px' }}>
                      {documents.length === 0
                        ? `No ${topTab === 'templates' ? 'templates' : 'uploaded documents'} yet`
                        : 'No items match current filters'}
                    </div>
                    {topTab === 'uploaded' && documents.length === 0 && (
                      <div style={{ fontSize: '11px', marginTop: '8px' }}>
                        Upload documents in the <strong>Documents</strong> tab first.
                      </div>
                    )}
                  </div>
                ) : (
                  filtered.map((d) => {
                    const a = parseAnalysis(d.ai_analysis);
                    const category = a?.document_classification?.primary_category || 'Unclassified';
                    return (
                      <ListItem
                        key={d.id}
                        selected={selected?.id === d.id}
                        onClick={() => selectDocument(d)}
                        title={d.original_filename || d.filename || '(unnamed)'}
                        subtitle={a?.summary?.slice(0, 60) || d.document_type}
                        meta={formatRelativeTime(d.updated_at || d.created_at)}
                        rightContent={
                          <span
                            style={{
                              background: `${colors.gold}22`,
                              color: colors.gold2,
                              fontSize: '9px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                            }}
                          >
                            {category}
                          </span>
                        }
                      />
                    );
                  })
                )}
              </div>
            </>
          }
          right={
            selected && editedAnalysis ? (
              <AnalysisEditor
                document={selected}
                analysis={editedAnalysis}
                activeTab={editorTab}
                onTabChange={setEditorTab}
                onUpdate={updateAnalysis}
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
              />
            ) : (
              <EmptyDetailState topTab={topTab} />
            )
          }
        />
      )}
    </>
  );
};

// ===========================================================================
// Analysis Editor — ProfileHeader + 6 sub-tabs
// ===========================================================================

interface AnalysisEditorProps {
  document: DocumentRecord;
  analysis: AIAnalysis;
  activeTab: string;
  onTabChange: (k: string) => void;
  onUpdate: (path: string, value: any) => void;
  isDirty: boolean;
  saving: boolean;
  onSave: () => void;
}

const AnalysisEditor: React.FC<AnalysisEditorProps> = ({
  document: doc,
  analysis,
  activeTab,
  onTabChange,
  onUpdate,
  isDirty,
  saving,
  onSave,
}) => {
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState(doc.filename || '');
  const [savingName, setSavingName] = useState(false);

  const handleSaveName = async () => {
    if (!editName.trim() || editName === doc.filename) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await axiosInstance.patch(`/documents/${doc.id}/rename`, { filename: editName.trim() });
      doc.filename = editName.trim();
      doc.original_filename = editName.trim();
      setEditingName(false);
    } catch { /* ignore */ }
    setSavingName(false);
  };

  return (
    <div>
      <ProfileHeader
        title={
          editingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                autoFocus
                style={{ fontSize: 16, fontWeight: 700, color: colors.navy, border: `1px solid ${colors.gold}`, borderRadius: 6, padding: '4px 10px', fontFamily: fonts.body, width: '100%' }}
              />
              <button onClick={handleSaveName} disabled={savingName} style={{ background: colors.gold, color: colors.navy, border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {savingName ? '...' : 'Save'}
              </button>
              <button onClick={() => setEditingName(false)} style={{ background: '#eee', color: '#666', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          ) : (
            <span style={{ cursor: 'pointer' }} onClick={() => { setEditName(doc.filename || doc.original_filename || ''); setEditingName(true); }} title="Click to rename">
              {doc.original_filename || doc.filename || '(unnamed)'} <FontAwesomeIcon icon={faPen} style={{ fontSize: 11, color: colors.muted, marginLeft: 4 }} />
            </span>
          )
        }
        metaItems={[
          { icon: faFileAlt, text: doc.document_type || '—' },
          ...(analysis.enriched_at
            ? [{ text: `Enriched ${formatRelativeTime(analysis.enriched_at)}` }]
            : []),
        ]}
        badges={
          analysis.document_classification?.primary_category ? (
            <span
              style={{
                background: `${colors.gold}22`,
                color: colors.gold,
                fontSize: '10px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '4px',
                textTransform: 'uppercase',
              }}
            >
              {analysis.document_classification.primary_category}
            </span>
          ) : null
        }
        rightContent={
          isDirty ? (
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
              <GoldButton onClick={onSave} disabled={saving}>
                <FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving…' : 'Save Changes'}
              </GoldButton>
            </div>
          ) : null
        }
      />

      <DetailTabs tabs={EDITOR_TABS} activeTab={activeTab} onChange={onTabChange} />

      <div style={{ padding: '0 24px 24px 24px' }}>
        {activeTab === 'summary' && <SummaryPane analysis={analysis} onUpdate={onUpdate} />}
        {activeTab === 'key-info' && <KeyInfoPane analysis={analysis} onUpdate={onUpdate} />}
        {activeTab === 'usage' && <UsageContextPane analysis={analysis} onUpdate={onUpdate} />}
        {activeTab === 'compliance' && <CompliancePane analysis={analysis} onUpdate={onUpdate} />}
        {activeTab === 'classification' && <ClassificationPane analysis={analysis} onUpdate={onUpdate} />}
        {activeTab === 'qa-keywords' && <QAKeywordsPane analysis={analysis} onUpdate={onUpdate} />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-tab panes
// ---------------------------------------------------------------------------

interface PaneProps {
  analysis: AIAnalysis;
  onUpdate: (path: string, value: any) => void;
}

const SummaryPane: React.FC<PaneProps> = ({ analysis, onUpdate }) => (
  <>
    <SectionCard title="Document Summary">
      <FormLabel>AI-Generated Summary</FormLabel>
      <WhiteTextarea
        rows={5}
        value={analysis.summary || ''}
        onChange={(e) => onUpdate('summary', e.target.value)}
        placeholder="Concise summary of what this document is and its primary purpose..."
      />
      <FormHelp>Feeds the RAG chatbot's high-level context about this document.</FormHelp>
    </SectionCard>

    <SectionCard title="Document Purpose">
      <WhiteInput
        value={analysis.document_purpose || ''}
        onChange={(e) => onUpdate('document_purpose', e.target.value)}
        placeholder="e.g. Establishes agency relationship with buyer"
      />
      <FormHelp>A one-line statement of why this document exists.</FormHelp>
    </SectionCard>
  </>
);

const KeyInfoPane: React.FC<PaneProps> = ({ analysis, onUpdate }) => {
  const ki = analysis.key_information || {};
  const selectedTypes = analysis.document_classification?.transaction_types || [];
  const selectedParties = ki.parties_involved || [];

  // Derive the context-aware parties button set based on selected
  // transaction types. Matches the original AIDocumentReview behavior.
  const relevantParties: string[] = (() => {
    const set = new Set<string>(ALWAYS_COMMON_PARTIES);
    if (selectedTypes.length === 0) {
      DEFAULT_PARTIES_WHEN_NO_TYPES.forEach((p) => set.add(p));
    } else {
      selectedTypes.forEach((type) => {
        (PARTIES_BY_TYPE[type] || []).forEach((p) => set.add(p));
      });
    }
    return Array.from(set).sort();
  })();

  // Any custom parties not in the predefined set (e.g. broker typed one manually)
  const customParties = selectedParties.filter(
    (p) => !relevantParties.some((rp) => rp.toLowerCase() === p.toLowerCase())
  );

  return (
    <>
      <SectionCard title="Transaction Types *">
        <InfoAlert>
          Select which transaction types require this document. This helps filter documents
          for specific workflows (e.g. rental vs purchase).
        </InfoAlert>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
          {TRANSACTION_TYPES.map((t) => {
            const isSelected = selectedTypes.includes(t.key);
            return (
              <PillButton
                key={t.key}
                selected={isSelected}
                title={t.tooltip}
                onClick={() => {
                  const next = isSelected
                    ? selectedTypes.filter((x) => x !== t.key)
                    : [...selectedTypes, t.key];
                  onUpdate('document_classification.transaction_types', next);
                }}
              >
                {isSelected && <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '5px' }} />}
                {t.key}
              </PillButton>
            );
          })}
        </div>
        <FormHelp>Select all transaction types where this document applies.</FormHelp>
      </SectionCard>

      <SectionCard title="Parties Involved">
        {selectedTypes.length > 0 ? (
          <InfoAlert>
            Showing parties for: <strong>{selectedTypes.join(', ')}</strong> transactions
          </InfoAlert>
        ) : (
          <WarnAlert>
            💡 <strong>Tip:</strong> Select transaction types above to see relevant parties for
            each type (e.g. Landlord/Tenant for rentals).
          </WarnAlert>
        )}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
          {relevantParties.map((party) => {
            const isSelected = selectedParties.some(
              (p) => p.toLowerCase() === party.toLowerCase()
            );
            return (
              <PillButton
                key={party}
                selected={isSelected}
                onClick={() => {
                  const next = isSelected
                    ? selectedParties.filter((p) => p.toLowerCase() !== party.toLowerCase())
                    : [...selectedParties, party];
                  onUpdate('key_information.parties_involved', next);
                }}
              >
                {isSelected && <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '5px' }} />}
                {party}
              </PillButton>
            );
          })}
        </div>
        <FormHelp>Select all parties involved in this document.</FormHelp>

        {customParties.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <InfoAlert>
              <strong>Other parties on file:</strong> {customParties.join(', ')}
            </InfoAlert>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Critical Terms">
        <WhiteTextarea
          rows={4}
          value={(ki.critical_terms || []).join('\n')}
          onChange={(e) => {
            // Keep empty lines while editing so the cursor behaves naturally;
            // we'll clean them on save via the Save button path (read-trim).
            onUpdate('key_information.critical_terms', e.target.value.split('\n'));
          }}
          onBlur={(e) => {
            // On blur, strip empty entries so the saved data is clean.
            const cleaned = e.target.value.split('\n').filter((s) => s.trim());
            onUpdate('key_information.critical_terms', cleaned);
          }}
          placeholder={`Enter critical terms (one per line):\n• Earnest money deposit\n• Contingency period\n• Closing date`}
        />
        <FormHelp>Key provisions, coverage details, or important conditions in this document.</FormHelp>
      </SectionCard>

      <SectionCard title="Dates">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div>
            <FormLabel>Effective Date</FormLabel>
            <WhiteInput
              type="text"
              value={ki.effective_date || ''}
              onChange={(e) => onUpdate('key_information.effective_date', e.target.value)}
              placeholder="YYYY-MM-DD or descriptive"
            />
          </div>
          <div>
            <FormLabel>Filing Deadline</FormLabel>
            <WhiteInput
              type="text"
              value={ki.filing_deadline || ''}
              onChange={(e) => onUpdate('key_information.filing_deadline', e.target.value)}
              placeholder="e.g. Within 5 days of contract"
            />
          </div>
        </div>
      </SectionCard>
    </>
  );
};

// Reusable pill button matching the original primary/outline-primary
// Bootstrap Button pattern from AIDocumentReview.tsx.
const PillButton: React.FC<{
  selected: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}> = ({ selected, onClick, title, children }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      background: selected
        ? `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`
        : colors.white,
      color: selected ? colors.navy : colors.gold,
      border: `1px solid ${selected ? colors.gold : `${colors.gold}66`}`,
      borderRadius: '6px',
      padding: '7px 14px',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: fonts.body,
      whiteSpace: 'nowrap',
      transition: 'all 0.12s',
      boxShadow: selected ? `0 2px 6px ${colors.gold}33` : 'none',
    }}
  >
    {children}
  </button>
);

const InfoAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      background: `${colors.blue}11`,
      border: `1px solid ${colors.blue}44`,
      borderLeft: `3px solid ${colors.blue}`,
      borderRadius: '6px',
      padding: '8px 12px',
      fontSize: '12px',
      color: '#555',
      marginBottom: '10px',
      lineHeight: 1.5,
    }}
  >
    <FontAwesomeIcon icon={faLightbulb} style={{ color: colors.blue, marginRight: '6px' }} />
    {children}
  </div>
);

const WarnAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      background: `${colors.orange}11`,
      border: `1px solid ${colors.orange}44`,
      borderLeft: `3px solid ${colors.orange}`,
      borderRadius: '6px',
      padding: '8px 12px',
      fontSize: '12px',
      color: '#555',
      marginBottom: '10px',
      lineHeight: 1.5,
    }}
  >
    {children}
  </div>
);

const UsageContextPane: React.FC<PaneProps> = ({ analysis, onUpdate }) => {
  const wr = analysis.usage_context?.when_required || {};
  const timingConditions = wr.timing_conditions || [];

  const addTimingCondition = () => {
    onUpdate('usage_context.when_required.timing_conditions', [
      ...timingConditions,
      { trigger: '', deadline: '', priority: 'medium' },
    ]);
  };
  const updateTimingCondition = (i: number, field: string, value: string) => {
    const next = [...timingConditions];
    next[i] = { ...next[i], [field]: value };
    onUpdate('usage_context.when_required.timing_conditions', next);
  };
  const removeTimingCondition = (i: number) => {
    onUpdate('usage_context.when_required.timing_conditions', timingConditions.filter((_, idx) => idx !== i));
  };

  return (
    <>
      <SectionCard title="When Required">
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '12px' }}>
          <input
            type="checkbox"
            checked={!!wr.always_required}
            onChange={(e) => onUpdate('usage_context.when_required.always_required', e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '13px', color: colors.navy, fontWeight: 500 }}>
            Always required (regardless of conditions)
          </span>
        </label>

        <FormLabel>Transaction Stages</FormLabel>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
          {TRANSACTION_STAGES.map((s) => {
            const selected = wr.transaction_stages?.includes(s.key);
            return (
              <button
                key={s.key}
                onClick={() => {
                  const current = wr.transaction_stages || [];
                  const next = selected ? current.filter((x) => x !== s.key) : [...current, s.key];
                  onUpdate('usage_context.when_required.transaction_stages', next);
                }}
                style={{
                  background: selected ? colors.gold : `${colors.gold}22`,
                  color: selected ? colors.navy : colors.gold,
                  border: `1px solid ${selected ? colors.gold : `${colors.gold}55`}`,
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: fonts.body,
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <FormHelp>Which pipeline stages require this document.</FormHelp>
      </SectionCard>

      <SectionCard title="Timing Conditions">
        <FormHelp>Triggers that make this document required. Used by Agent Kairos automation.</FormHelp>
        {timingConditions.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: '12px', padding: '10px 0' }}>
            No timing conditions. Add one below.
          </div>
        ) : (
          timingConditions.map((c, i) => (
            <div
              key={i}
              style={{
                background: colors.off,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '10px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase' }}>
                  Condition {i + 1}
                </div>
                <button
                  onClick={() => removeTimingCondition(i)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${colors.red}55`,
                    color: colors.red,
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} /> Remove
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <FormLabel>Trigger Event</FormLabel>
                  <select
                    value={c.trigger || ''}
                    onChange={(e) => updateTimingCondition(i, 'trigger', e.target.value)}
                    style={{
                      width: '100%',
                      background: colors.white,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      padding: '7px 10px',
                      fontSize: '12px',
                      color: colors.navy,
                      fontFamily: fonts.body,
                    }}
                  >
                    <option value="">Select trigger…</option>
                    {TIMING_TRIGGERS.map((group) => (
                      <optgroup key={group.group} label={group.group}>
                        {group.options.map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <FormLabel>Priority</FormLabel>
                  <select
                    value={c.priority || 'medium'}
                    onChange={(e) => updateTimingCondition(i, 'priority', e.target.value)}
                    style={{
                      width: '100%',
                      background: colors.white,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      padding: '7px 10px',
                      fontSize: '12px',
                      color: colors.navy,
                      fontFamily: fonts.body,
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '10px' }}>
                <FormLabel>Deadline Description</FormLabel>
                <WhiteInput
                  value={c.deadline || ''}
                  onChange={(e) => updateTimingCondition(i, 'deadline', e.target.value)}
                  placeholder="e.g. Within 3 business days"
                />
              </div>
            </div>
          ))
        )}
        <OutlineButton onClick={addTimingCondition}>
          <FontAwesomeIcon icon={faPlus} /> Add Timing Condition
        </OutlineButton>
      </SectionCard>

      <SectionCard title="Who Needs This Document">
        <FormLabel>Signing Parties</FormLabel>
        <ArrayEditor
          items={analysis.usage_context?.who_needs?.signing_parties || []}
          onChange={(items) => onUpdate('usage_context.who_needs.signing_parties', items)}
          placeholder="e.g. Buyer, Seller"
        />
        <div style={{ height: '12px' }} />
        <FormLabel>Reviewing Parties</FormLabel>
        <ArrayEditor
          items={analysis.usage_context?.who_needs?.reviewing_parties || []}
          onChange={(items) => onUpdate('usage_context.who_needs.reviewing_parties', items)}
          placeholder="e.g. Broker, Attorney"
        />
      </SectionCard>

      <SectionCard title="Why Important">
        <FormLabel>Legal Requirements</FormLabel>
        <ArrayEditor
          items={analysis.usage_context?.why_important?.legal_requirements || []}
          onChange={(items) => onUpdate('usage_context.why_important.legal_requirements', items)}
          placeholder="e.g. ADRE R4-28-802"
        />
        <div style={{ height: '12px' }} />
        <FormLabel>Risks If Missing</FormLabel>
        <ArrayEditor
          items={analysis.usage_context?.why_important?.risks_if_missing || []}
          onChange={(items) => onUpdate('usage_context.why_important.risks_if_missing', items)}
          placeholder="e.g. Transaction cannot close"
        />
      </SectionCard>
    </>
  );
};

const CompliancePane: React.FC<PaneProps> = ({ analysis, onUpdate }) => {
  const cr = analysis.compliance_requirements || {};
  return (
    <>
      <SectionCard title="Compliance Flags">
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!cr.state_specific}
            onChange={(e) => onUpdate('compliance_requirements.state_specific', e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '13px', color: colors.navy, fontWeight: 500 }}>
            State-specific requirements apply
          </span>
        </label>
      </SectionCard>

      <SectionCard title="Legal References">
        <ArrayEditor
          items={cr.legal_references || []}
          onChange={(items) => onUpdate('compliance_requirements.legal_references', items)}
          placeholder="e.g. A.R.S. § 32-2151, Fair Housing Act"
        />
        <FormHelp>Statutes and regulations this document satisfies or relies on.</FormHelp>
      </SectionCard>
    </>
  );
};

const ClassificationPane: React.FC<PaneProps> = ({ analysis, onUpdate }) => {
  const dc = analysis.document_classification || {};
  return (
    <SectionCard title="Classification">
      <FormLabel>Primary Category</FormLabel>
      <select
        value={dc.primary_category || ''}
        onChange={(e) => onUpdate('document_classification.primary_category', e.target.value)}
        style={{
          width: '100%',
          background: colors.off,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '8px 12px',
          fontSize: '13px',
          color: colors.navy,
          marginBottom: '14px',
          fontFamily: fonts.body,
        }}
      >
        <option value="">Select category…</option>
        <option value="required">Required</option>
        <option value="conditional">Conditional</option>
        <option value="optional">Optional</option>
        <option value="supplemental">Supplemental</option>
      </select>

      <FormLabel>Subcategory</FormLabel>
      <WhiteInput
        value={dc.subcategory || ''}
        onChange={(e) => onUpdate('document_classification.subcategory', e.target.value)}
        placeholder="e.g. Agency disclosure, Financing contingency"
        style={{ marginBottom: '14px' }}
      />

      <FormLabel>Required Frequency</FormLabel>
      <select
        value={dc.required_frequency || ''}
        onChange={(e) => onUpdate('document_classification.required_frequency', e.target.value)}
        style={{
          width: '100%',
          background: colors.off,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '8px 12px',
          fontSize: '13px',
          color: colors.navy,
          fontFamily: fonts.body,
        }}
      >
        <option value="">Select frequency…</option>
        <option value="every_transaction">Every Transaction</option>
        <option value="per_party">Per Party</option>
        <option value="once_per_client">Once Per Client</option>
        <option value="conditional">Only When Conditions Met</option>
      </select>
    </SectionCard>
  );
};

const QAKeywordsPane: React.FC<PaneProps> = ({ analysis, onUpdate }) => {
  const qa = analysis.chatbot_qa || [];

  const addQA = () => onUpdate('chatbot_qa', [...qa, { question: '', answer: '' }]);
  const updateQA = (i: number, field: 'question' | 'answer', value: string) => {
    const next = [...qa];
    next[i] = { ...next[i], [field]: value };
    onUpdate('chatbot_qa', next);
  };
  const removeQA = (i: number) => onUpdate('chatbot_qa', qa.filter((_, idx) => idx !== i));

  return (
    <>
      <SectionCard title="Search Keywords">
        <FormHelp>Keywords the chatbot uses for retrieval. Add one at a time or paste comma-separated.</FormHelp>
        <ArrayEditor
          items={analysis.search_keywords || []}
          onChange={(items) => onUpdate('search_keywords', items)}
          placeholder="e.g. earnest money, deposit, contingency"
        />
      </SectionCard>

      <SectionCard title="Chatbot Q&A Pairs">
        <FormHelp>
          Pre-computed question/answer pairs the Broker Chat Bot can serve directly when
          agents ask about this document.
        </FormHelp>
        {qa.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: '12px', padding: '10px 0' }}>
            No Q&amp;A pairs yet. Add your first one below.
          </div>
        ) : (
          qa.map((pair, i) => (
            <div
              key={i}
              style={{
                background: colors.off,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '10px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase' }}>
                  Q&amp;A {i + 1}
                </div>
                <button
                  onClick={() => removeQA(i)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${colors.red}55`,
                    color: colors.red,
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} /> Remove
                </button>
              </div>
              <FormLabel>Question</FormLabel>
              <WhiteInput
                value={pair.question || ''}
                onChange={(e) => updateQA(i, 'question', e.target.value)}
                placeholder="e.g. When is earnest money due?"
                style={{ marginBottom: '8px' }}
              />
              <FormLabel>Answer</FormLabel>
              <WhiteTextarea
                rows={3}
                value={pair.answer || ''}
                onChange={(e) => updateQA(i, 'answer', e.target.value)}
                placeholder="Detailed answer the chatbot will serve…"
              />
            </div>
          ))
        )}
        <OutlineButton onClick={addQA}>
          <FontAwesomeIcon icon={faPlus} /> Add Q&amp;A Pair
        </OutlineButton>
      </SectionCard>
    </>
  );
};

// ===========================================================================
// Compliance Rules pane (3rd top-level tab)
// ===========================================================================

const ComplianceRulesPane: React.FC = () => (
  <div style={{ flex: 1, background: colors.offBg, overflowY: 'auto', padding: '28px 32px' }}>
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div
        style={{
          background: colors.white,
          border: `1px solid ${colors.border}`,
          borderLeft: `4px solid ${colors.gold}`,
          borderRadius: '10px',
          padding: '32px',
          textAlign: 'center',
        }}
      >
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          style={{ color: colors.gold2, fontSize: '36px', marginBottom: '12px' }}
        />
        <h2
          style={{
            fontFamily: fonts.display,
            fontSize: '28px',
            color: colors.navy,
            margin: '0 0 8px 0',
          }}
        >
          Compliance Rules
        </h2>
        <p style={{ fontSize: '13px', color: colors.muted, margin: '0 0 20px 0', lineHeight: 1.6 }}>
          Brokerage-specific compliance rule sets define what your brokerage enforces beyond the
          baseline state regulations. Rules feed both the AI auditor and the chatbot.
        </p>
        <p style={{ fontSize: '12px', color: colors.muted }}>
          Full rule-set editor ships in a follow-up pass. Rules are currently managed via
          <strong> /broker-intelligence/rules</strong> on the legacy hub.
        </p>
      </div>
    </div>
  </div>
);

// ===========================================================================
// Helpers
// ===========================================================================

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        color: colors.navy,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '10px',
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const FormLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label
    style={{
      display: 'block',
      fontSize: '11px',
      fontWeight: 600,
      color: colors.muted,
      marginBottom: '5px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}
  >
    {children}
  </label>
);

const FormHelp: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: '11px', color: colors.muted, marginTop: '6px', fontStyle: 'italic' }}>
    {children}
  </div>
);

const WhiteInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <NavyInput
    {...props}
    style={{
      background: colors.off,
      color: colors.navy,
      border: `1px solid ${colors.border}`,
      ...props.style,
    }}
  />
);

const WhiteTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <NavyTextarea
    {...props}
    style={{
      background: colors.off,
      color: colors.navy,
      border: `1px solid ${colors.border}`,
      ...props.style,
    }}
  />
);

// Reusable array-of-strings editor (for parties, terms, keywords, etc.)
const ArrayEditor: React.FC<{
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}> = ({ items, onChange, placeholder }) => {
  const [input, setInput] = useState('');

  const addItem = () => {
    const val = input.trim();
    if (!val) return;
    // Support comma-separated paste
    const parts = val.split(',').map((p) => p.trim()).filter(Boolean);
    onChange([...items, ...parts]);
    setInput('');
  };

  const removeItem = (i: number) => {
    onChange(items.filter((_, idx) => idx !== i));
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
        {items.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: '12px' }}>None yet</div>
        ) : (
          items.map((item, i) => (
            <span
              key={i}
              style={{
                background: `${colors.gold}22`,
                color: colors.gold,
                fontSize: '12px',
                fontWeight: 500,
                padding: '4px 10px',
                borderRadius: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {item}
              <button
                onClick={() => removeItem(i)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.gold,
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '11px',
                }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </span>
          ))
        )}
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <WhiteInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
          placeholder={placeholder || 'Type and press Enter'}
        />
        <OutlineButton onClick={addItem} style={{ whiteSpace: 'nowrap' }}>
          <FontAwesomeIcon icon={faPlus} /> Add
        </OutlineButton>
      </div>
    </div>
  );
};

function parseAnalysis(raw: any): AIAnalysis | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

const EmptyDetailState: React.FC<{ topTab: string }> = ({ topTab }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '48px',
      color: colors.muted,
    }}
  >
    <FontAwesomeIcon icon={faRobot} style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold3 }} />
    <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>
      Select a {topTab === 'templates' ? 'template' : 'document'} to enrich
    </div>
    <div style={{ fontSize: '12px', marginTop: '4px', textAlign: 'center' }}>
      Edit the structured AI analysis across<br />
      6 tabs so the Broker Chat Bot can learn from it
    </div>
  </div>
);

export default AIReviewTab;
